import { Col, Row, Space } from 'antd';
import { Formik, type FormikHelpers, useFormikContext } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type ApplicationAgreementDataFragment } from '../../../../api/fragments/ApplicationAgreementData';
import { usePrefetchAgreementsForEventQuery } from '../../../../api/queries/prefetchAgreementsForEvent';
import { usePrefetchKycFieldsForEventQuery } from '../../../../api/queries/prefetchKYCFieldsForEvent';
import { AssetCondition, CompanyTheme, CustomerKind, type LocalCustomerField } from '../../../../api/types';
import { useAccount } from '../../../../components/contexts/AccountContextManager';
import Form from '../../../../components/fields/Form';
import FormAutoTouch from '../../../../components/FormAutoTouch';
import PortalLoadingElement from '../../../../components/PortalLoadingElement';
import BasicProLayoutContainer from '../../../../layouts/BasicProLayout/BasicProLayoutContainer';
import { useThemeComponents } from '../../../../themes';
import { type UploadDocumentProp, getInitialValues, prepareKYCFieldPayload } from '../../../../utilities/kycPresets';
import useTranslatedString from '../../../../utilities/useTranslatedString';
import useValidator from '../../../../utilities/useValidator';
import { getApplicantAgreements } from '../../../shared/CIPage/ConsentAndDeclarations/getAgreements';
import {
    getAgreementValues,
    mergeAgreementValues,
} from '../../../shared/CIPage/ConsentAndDeclarations/useAgreementsValues';
import CustomerDetails from '../../../shared/JourneyPage/CustomerDetails';
import ResetKYCButtonPorsche from '../../../shared/JourneyPage/CustomerDetails/ResetKYCButtonPorsche';
import JourneySectionWrapper from '../../../shared/JourneyPage/JourneySectionWrapper';
import JourneySteps from '../../../shared/JourneyPage/JourneySteps';
import DealerInfo from '../../ConfiguratorApplicationEntrypoint/DealerInfo';
import { JourneyStage } from '../../FinderApplicationPublicAccessEntrypoint/types';
import { type State } from '../../StandardApplicationEntrypoint/Journey/reducer';
import { getApplicantKyc } from '../../StandardApplicationEntrypoint/KYCPage/getKyc';
import { SectionDivider } from '../../StandardApplicationEntrypoint/KYCPage/shared';
import { NextButton } from '../../StandardApplicationEntrypoint/shared/JourneyButton';
import { StyledJourneyToolbar } from '../../StandardApplicationEntrypoint/styledComponents';
import { useEventJourneyKycAndAgreementContext } from '../Entrypoint/EventJourneyKycAndAgreement';
import { useEventJourneySetupContext } from '../Entrypoint/EventJourneySetup';
import { type EventApplicationState } from '../Journey/JourneyController';
import ConsentsAndDeclarations from './ConsentsAndDeclarations';
import OcrAndMyinfo from './CustomerDetails/OcrAndMyInfo';
import CustomizedFieldsSection from './CustomizedFieldsSection';
import LiveChat from './LiveChat';
import { type ApplicantFormValues } from './shared';
import useEventApplicantValidator from './useEventApplicantValidator';
import VehicleInterest from './VehicleInterest';

const leftColSpan = { xl: 8, lg: 12, md: 24 };
const rightColSpan = { xl: 16, lg: 12, md: 24 };

export type Agreements = ApplicationAgreementDataFragment[];

export type InnerFormProps = {
    state?: State<EventApplicationState>;
} & Partial<UploadDocumentProp>;

const InnerForm = ({ state, uploadDocument, removeDocument }: InnerFormProps) => {
    const navigate = useNavigate();
    const { t } = useTranslation([
        'eventApplicantForm',

        // Load it first, so that when there is test drive
        // It won't trigger portal loading
        'appointmentPage',
    ]);
    const translatedString = useTranslatedString();

    const { event, eventModule, endpoint, liveChatSetting, showResetKYCButton, variants, showLiveChat, journeyStages } =
        useEventJourneySetupContext();
    const {
        kycState,
        setKycState,
        agreementState,
        setAgreementState,
        customerKind,
        hasGuarantorPreset,
        isCorporateCustomer,
        setIsCorporate,
        setPrefill,
    } = useEventJourneyKycAndAgreementContext();

    const { theme, BackButton, StandardLayout } = useThemeComponents();

    const {
        initialValues,
        values,
        isSubmitting,
        setFieldTouched,
        resetForm,
        handleSubmit,
        setFieldValue,
        validateForm,
        submitForm,
    } = useFormikContext<ApplicantFormValues>();

    const [withMyInfo, setWithMyInfo] = useState(false);

    // Only capture the first loading for both kyc and agreement
    // This to prevent flicker on the front side
    const [isKycLoaded, setKycLoaded] = useState(false);
    const [isAgreementLoaded, setAgreementLoaded] = useState(false);

    // Fetch KYC
    usePrefetchKycFieldsForEventQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            urlSlug: event.urlSlug,
            configuration: {
                assetCondition: AssetCondition.New,
                testDrive: values.configuration.testDrive,
                tradeIn: values.configuration.tradeIn,
            },
            dealerId: values.dealerId,
            eventModuleId: eventModule.id,
        },
        // Skip when kyc step is for guarantor
        skip: customerKind === CustomerKind.Guarantor,
        onCompleted: data => {
            const applicantKyc = getApplicantKyc(data.applicantKYC ?? []);
            const corporateKyc = getApplicantKyc(data.corporateKYC ?? []);
            const guarantorKyc = getApplicantKyc(data.guarantorKYC ?? []);
            const activeKyc = isCorporateCustomer ? corporateKyc : applicantKyc;

            const newCustomerFields = getInitialValues(
                prepareKYCFieldPayload(values.customer.fields, true) as LocalCustomerField[],
                activeKyc
            );

            // Set outer state, so it align with validator
            setKycState({
                applicant: applicantKyc,
                corporate: corporateKyc,
                active: activeKyc,
                guarantor: guarantorKyc,
            });

            // Set formik value
            if (!isKycLoaded) {
                setFieldValue('customer.fields', newCustomerFields);
            }

            // Make sure it's shown loading portal once
            setKycLoaded(oldLoaded => (!oldLoaded ? true : oldLoaded));
        },
    });

    // Fetch Agreements
    usePrefetchAgreementsForEventQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            urlSlug: event.urlSlug,
            configuration: {
                assetCondition: AssetCondition.New,
                testDrive: values.configuration.testDrive,
                tradeIn: values.configuration.tradeIn,
            },
            dealerId: values.dealerId,
            eventModuleId: eventModule.id,
        },

        // Skip when kyc step is for guarantor
        skip: customerKind === CustomerKind.Guarantor,
        onCompleted: data => {
            const activeAgreements: ApplicationAgreementDataFragment[] = isCorporateCustomer
                ? getApplicantAgreements(data.corporateAgreements)
                : getApplicantAgreements(data.applicantAgreements);

            // Get remaining agreements that align with active one
            const newAgreementValues = getAgreementValues(mergeAgreementValues(activeAgreements, values.agreements));

            // Set outer state, so it align with validator
            setAgreementState({
                values: newAgreementValues,
                data: activeAgreements,
            });

            // Set formik value
            if (!isAgreementLoaded) {
                setFieldValue('agreements', newAgreementValues);
            }

            // Make sure it's shown loading portal once
            setAgreementLoaded(oldLoaded => (!oldLoaded ? true : oldLoaded));
        },
    });

    // Touch customer fields on fields change
    useEffect(() => {
        setFieldTouched('customer.fields', true);
    }, [kycState.active, setFieldTouched]);

    // Touch agreements fields on fields change
    useEffect(() => {
        setFieldTouched('agreements', true);
    }, [agreementState.data, setFieldTouched]);

    const resetFormHandler = useCallback(() => {
        resetForm({
            values: {
                ...initialValues,
                customer: { fields: getInitialValues([], kycState.active) },
                agreements: { ...values.agreements },
            },
        });
    }, [initialValues, kycState.active, resetForm, values.agreements]);

    const backIcon = state ? null : <BackButton type="link"> {t('eventApplicantForm:actions.back')}</BackButton>;

    const sectionDivider = useMemo(
        () =>
            event.showDealership ? (
                <Col span={24}>
                    <SectionDivider />
                </Col>
            ) : null,
        [event.showDealership]
    );

    const onSubmit = useCallback(async () => {
        await validateForm();
        await submitForm();
    }, [submitForm, validateForm]);

    const user = useAccount(true);
    const showCommentsToBank = user && state?.application?.bank?.showCommentsField;

    // Only show loading for the first one
    if (!isKycLoaded || !isAgreementLoaded) {
        return <PortalLoadingElement />;
    }

    return (
        <StandardLayout
            backIcon={backIcon}
            onBack={event.privateAccess ? () => navigate('..') : null}
            title={translatedString(event.name)}
            hasFooterBar
        >
            <BasicProLayoutContainer>
                <JourneySteps
                    currentStage={state?.stage || JourneyStage.ApplicantKYC}
                    stages={state?.stages || journeyStages}
                />
                <FormAutoTouch />
                <Form data-cy="leadGenFormId" id="applicantForm" name="applicantForm" onSubmitCapture={handleSubmit}>
                    <Row gutter={[24, 50]}>
                        <Col {...leftColSpan} style={{ padding: '0 12px' }}>
                            <Row gutter={[50, 40]}>
                                <VehicleInterest customerKind={customerKind} event={event} variants={variants} />
                                {event.showDealership && (
                                    <Col span={24}>
                                        <DealerInfo
                                            dealer={event.dealers.find(dealer => dealer.id === values.dealerId)}
                                        />
                                    </Col>
                                )}

                                <OcrAndMyinfo
                                    dealerId={values.dealerId}
                                    endpoint={endpoint}
                                    event={event}
                                    kycPresets={kycState.active}
                                    setWithMyInfo={setWithMyInfo}
                                    state={state}
                                    topDivider={sectionDivider}
                                    withMyInfo={withMyInfo}
                                />
                            </Row>
                        </Col>
                        <Col {...rightColSpan} style={{ padding: '0 12px' }}>
                            <JourneySectionWrapper
                                applicationType="EventApplication"
                                stage={state?.stage || JourneyStage.ApplicantKYC}
                                stages={state?.stages || journeyStages}
                            >
                                <Space direction="vertical" size={24} style={{ width: '100%' }}>
                                    <CustomerDetails
                                        customerKind={customerKind}
                                        gutter={24}
                                        hasGuarantorPreset={hasGuarantorPreset}
                                        hasUploadDocuments={false}
                                        hasVSOUpload={false}
                                        isApplyingFromDetails={false}
                                        kycPresets={kycState.active}
                                        removeDocument={removeDocument}
                                        resetFormHandler={resetFormHandler}
                                        setIsCorporate={setIsCorporate}
                                        setPrefill={setPrefill}
                                        showCommentsToInsurer={false}
                                        showRemarks={showCommentsToBank}
                                        showResetButton={showResetKYCButton && !withMyInfo}
                                        showTabs={kycState.corporate?.length > 0}
                                        showTitle={false}
                                        uploadDocument={uploadDocument}
                                        withFinancing={false}
                                    />
                                    {values.customizedFields.length > 0 && <CustomizedFieldsSection />}
                                    <ConsentsAndDeclarations applicationAgreements={agreementState.data} hideDivider />
                                </Space>
                            </JourneySectionWrapper>
                        </Col>
                    </Row>
                </Form>
                {showLiveChat && <LiveChat chatSetting={liveChatSetting} variants={variants} />}
            </BasicProLayoutContainer>
            <StyledJourneyToolbar>
                {showResetKYCButton &&
                    !withMyInfo &&
                    (theme === CompanyTheme.Porsche || theme === CompanyTheme.PorscheV3) && (
                        <ResetKYCButtonPorsche onConfirm={resetFormHandler} />
                    )}
                <NextButton
                    data-cy="leadGenFormButton"
                    disabled={isSubmitting}
                    form="applicantForm"
                    onSubmit={onSubmit}
                />
            </StyledJourneyToolbar>
        </StandardLayout>
    );
};

export type ApplicantFormProps = {
    initialValues: ApplicantFormValues;
    onSubmit: (values: ApplicantFormValues, helpers: FormikHelpers<ApplicantFormValues>) => void;
    state?: State<EventApplicationState>;
} & Partial<UploadDocumentProp>;

const ApplicantForm = ({ onSubmit, initialValues, ...rest }: ApplicantFormProps) => {
    const { agreementState, kycState, prefill } = useEventJourneyKycAndAgreementContext();
    const applicantValidations = useEventApplicantValidator(agreementState.data, kycState.active);

    const validate = useValidator(applicantValidations, { prefill });

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            <InnerForm {...rest} />
        </Formik>
    );
};

export default ApplicantForm;
